import React, { useState, useCallback, FC, useMemo } from 'react';
import { Form, Input, notification, Button, PageHeading, useMe } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { useUpdateCompanyInfo } from 'src/models/company/hooks';
import { faBuilding } from '@fortawesome/pro-light-svg-icons';
import { debounce } from 'lodash';
import { useApp, UserType } from 'src/models/auth';

interface FormState {
  companyName: string;
  orgNumber: string;
}

export const CompanySettings: FC = () => {
  const { permission, authUser } = useApp();
  const { data: paidUser } = useMe();
  const isPaid = useMemo(() => permission === UserType.Paid && !!authUser, [authUser, permission]);
  const companyId = useMemo(() => (paidUser ? paidUser.company.id : null), [paidUser]);
  const { t } = useTranslation();
  const [form] = Form.useForm<FormState>();
  const [touched, setTouched] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateCompanyInfo] = useUpdateCompanyInfo();

  const initialValues = useMemo((): FormState => {
    return {
      companyName: paidUser ? paidUser.company.companyName ?? '' : '',
      orgNumber: paidUser ? paidUser?.company.orgNumber ?? '' : ''
    };
  }, [paidUser]);

  const readOnly = useMemo(() => (paidUser ? !paidUser.isAdmin : false), [paidUser]);

  const onFinish = useCallback(
    (values: FormState) => {
      const { companyName, orgNumber } = values;
      setIsUpdating(() => true);
      if (isPaid) {
        companyId &&
          updateCompanyInfo({ variables: { id: companyId, companyName, orgNumber } })
            .then(() => {
              form.resetFields();
            })
            .catch(() => {
              notification.error({
                description: t('Common.unknownErrorDesc'),
                message: t('Common.unknownError')
              });
            })
            .finally(() => {
              setIsUpdating(() => false);
            });
      }
    },
    [companyId, form, isPaid, t, updateCompanyInfo]
  );

  const onValuesChange = useMemo(
    () =>
      debounce((changedValues: Partial<FormState>) => {
        if (
          (changedValues.companyName && changedValues.companyName !== initialValues.companyName) ||
          (changedValues.orgNumber && changedValues.orgNumber !== initialValues.orgNumber)
        ) {
          setTouched(true);
        } else {
          setTouched(false);
        }
      }, 300),
    [initialValues.companyName, initialValues.orgNumber]
  );

  return (
    <>
      <PageHeading title={t('Company.company')} desc={t('Company.companySettingsDesc')} icon={faBuilding} />
      <div className={styles.contentInner}>
        <h3 className={styles.contentTitle}>
          <span>{t('Common.profile')}</span>
        </h3>
        <p className={styles.contentDesc}>{t('Company.companyFormDesc')}</p>
        <Form
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          className={styles.formContent}
          layout={'vertical'}
          initialValues={initialValues}
        >
          <Form.Item
            label={t('Company.companyName')}
            name={'companyName'}
            rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Company.companyName') }) }]}
          >
            <Input type={'text'} data-name={'companyName'} placeholder={t('Company.companyName')} disabled={readOnly} />
          </Form.Item>
          <Form.Item
            name={'orgNumber'}
            label={t('Company.orgNumber')}
            rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Company.orgNumber') }) }]}
          >
            <Input
              readOnly={readOnly}
              type={'text'}
              data-name={'orgNumber'}
              placeholder={t('Company.orgNumber')}
              disabled={readOnly}
            />
          </Form.Item>
          <div className={styles.formSubmit}>
            <Button type={'primary'} htmlType={'submit'} loading={isUpdating} disabled={!touched}>
              {t('Common.updateYourProfile')}
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default CompanySettings;
